import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { authApi } from '@/pages/public/auth/auth-api';

type SecuritySessionContextValue = {
    /** Whether sensitive Settings > Security actions are currently unlocked (recent re-authentication). */
    isElevated: boolean;
    /** Epoch ms when the elevation expires, or null when not elevated. */
    expiresAt: number | null;
    /** True until the first status check resolves. */
    loading: boolean;
    /**
     * Whether the request carries a Better Auth session at all. False only for a legacy-only session
     * (signed in before Better Auth existed, or through the legacy login page), which cannot use the
     * Better Auth powered security screens and is sent to the legacy ones instead.
     */
    hasSession: boolean;
    /** Re-check elevation with the backend (call after a password re-auth or an SSO re-auth return). */
    refresh: () => Promise<boolean>;
};

const SecuritySessionContext = createContext<SecuritySessionContextValue | null>(null);

/**
 * Tracks the backend-owned "security-session" (step-up re-auth) so the UI can gate Settings > Security
 * and the Delete-account section. The backend is the source of truth — this only mirrors the status
 * endpoint and flips to stale exactly when the elevation window ends.
 */
export function SecuritySessionProvider({ children }: { children: ReactNode }) {
    const [isElevated, setIsElevated] = useState(false);
    const [expiresAt, setExpiresAt] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    // Starts (and stays, on error) true: only an explicit `hasSession: false` from the backend routes a
    // user to the legacy screens, so a transient failure never drags a Better Auth user back there.
    const [hasSession, setHasSession] = useState(true);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const refresh = useCallback(async () => {
        try {
            const status = await authApi.getSecuritySessionStatus();
            setIsElevated(status.elevated);
            setExpiresAt(status.expiresAt);
            setHasSession(status.hasSession !== false);
            return status.elevated;
        } catch {
            // A transient error → treat as not elevated, but do not claim there is no session.
            setIsElevated(false);
            setExpiresAt(null);
            return false;
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        void refresh();
    }, [refresh]);

    // Flip to stale exactly when the window ends — no polling.
    useEffect(() => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
        if (isElevated && expiresAt) {
            const ms = expiresAt - Date.now();
            if (ms <= 0) {
                setIsElevated(false);
            } else {
                timerRef.current = setTimeout(() => setIsElevated(false), ms);
            }
        }
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, [isElevated, expiresAt]);

    const value = useMemo(
        () => ({ isElevated, expiresAt, loading, hasSession, refresh }),
        [isElevated, expiresAt, loading, hasSession, refresh],
    );

    return <SecuritySessionContext.Provider value={value}>{children}</SecuritySessionContext.Provider>;
}

export function useSecuritySession() {
    const ctx = useContext(SecuritySessionContext);
    if (!ctx) {
        throw new Error('useSecuritySession must be used within a SecuritySessionProvider');
    }
    return ctx;
}
